import type { Database } from '~/types/database'
import { PLAN_FEATURES, PLAN_LIMITS, type PlanName, type LimitKey, type AddonKey, getRequiredPlan } from '~/composables/usePlanConfig'
import { resolveStaffRole, type StaffRole } from '~~/shared/utils/staffRoles'

export interface AddonState {
  active: boolean
  status: 'active' | 'past_due' | 'canceled' | null
  source: 'stripe' | 'grandfathered' | 'admin' | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
}

interface SubscriptionData {
  plan: PlanName
  status: string
  isActive: boolean
  cancelAtPeriodEnd: boolean
  currentPeriodEnd: string | null
  hasUsedTrial: boolean
  downgradedAt: string | null
  previousPlan: string | null
  paymentType: 'subscription' | 'lifetime'
  isLifetime: boolean
  usage: Record<string, { current: number, limit: number | null }>
  addons: Record<AddonKey, AddonState>
}

interface CharacterBlock {
  reason: string
  blocked_at: string
  appeal_submitted_at: string | null
}

export interface OnboardingData {
  completed_steps: string[]
}

interface CharacterWhitelist {
  whitelisted_at: string
}

interface Character {
  id: string
  uid: number
  username: string | null
  views: number
  block: CharacterBlock | null
  whitelist: CharacterWhitelist | null
  discord_user_id: string | null
  youtube_channel_id: string | null
  twitch_user_id: string | null
  onboarding_completed: boolean
  onboarding: OnboardingData
  created_at: string
  updated_at: string
}

export interface BackgroundSettings {
  opacity: number
  blur: number
  gradientColor: string
  gradientOpacity: number
  effect: string
  positionX: number
  positionY: number
  scale: number
}

export const DEFAULT_BACKGROUND_SETTINGS: BackgroundSettings = {
  opacity: 100,
  blur: 0,
  gradientColor: '',
  gradientOpacity: 60,
  effect: 'none',
  positionX: 50,
  positionY: 50,
  scale: 100
}

export interface TextSettings {
  color: string
  fontScale: number
  fontWeight: string
  fontFamily: string
}

export const DEFAULT_TEXT_SETTINGS: TextSettings = {
  color: '#FDFDFB',
  fontScale: 1.0,
  fontWeight: '400',
  fontFamily: 'Rajdhani'
}

export interface AudioSettings {
  shuffle: boolean
  volume: number
  enterLabel: string
}

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  shuffle: false,
  volume: 0.5,
  enterLabel: ''
}

export interface CursorSettings {
  trail: string
}

export const DEFAULT_CURSOR_SETTINGS: CursorSettings = {
  trail: 'none'
}

export interface YouTubeEmbedSettings {
  displayMode: 'latest' | 'pinned'
  videoCount: number
  showBanner: boolean
  pinnedVideoIds: string[]
}

export const DEFAULT_YOUTUBE_EMBED_SETTINGS: YouTubeEmbedSettings = {
  displayMode: 'latest',
  videoCount: 3,
  showBanner: true,
  pinnedVideoIds: []
}

export interface TwitchEmbedSettings {
  showClips: boolean
  clipCount: number
  showLiveStatus: boolean
  showBanner: boolean
  showTags: boolean
  showDescription: boolean
  showSchedule: boolean
  showSubscriberCount: boolean
}

export const DEFAULT_TWITCH_EMBED_SETTINGS: TwitchEmbedSettings = {
  showClips: true,
  clipCount: 3,
  showLiveStatus: true,
  showBanner: true,
  showTags: true,
  showDescription: false,
  showSchedule: false,
  showSubscriberCount: true
}

export interface SpotifyEmbedSettings {
  showNowPlaying: boolean
  showTopTracks: boolean
  topTracksCount: number
  topTracksTimeRange: 'short_term' | 'medium_term' | 'long_term'
}

export const DEFAULT_SPOTIFY_EMBED_SETTINGS: SpotifyEmbedSettings = {
  showNowPlaying: true,
  showTopTracks: true,
  topTracksCount: 5,
  topTracksTimeRange: 'medium_term'
}

export interface CommentSettings {
  allowComments: 'everyone' | 'friends_only'
}

export const DEFAULT_COMMENT_SETTINGS: CommentSettings = {
  allowComments: 'everyone'
}

export type WidgetPosition = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'

export interface SingleWidgetSettings {
  position: WidgetPosition
  visible: boolean
}

export interface WidgetSettings {
  audio: SingleWidgetSettings
  views: SingleWidgetSettings
  comments: SingleWidgetSettings
}

export const DEFAULT_WIDGET_SETTINGS: WidgetSettings = {
  audio: { position: 'bottom-left', visible: true },
  views: { position: 'bottom-left', visible: false },
  comments: { position: 'bottom-right', visible: true }
}

export interface ThemeSettings {
  primaryColor: string
  cardColor: string
  cardOpacity: number
  cardBlur: number
  cardBorderStyle: string
  cardBorderColor: string
  cardBorderRadius: number
  iconStyle: string
  iconSize: number
  linkStyle: string
  avatarShape: string
  avatarSize: number
  avatarBorder: string
  avatarBlur: number
  avatarPositionX: number
  avatarPositionY: number
  avatarScale: number
  separatorStyle: string
  cardAnimation: string
  cardHover: string
}

export const DEFAULT_THEME_SETTINGS: ThemeSettings = {
  primaryColor: '#8B7AE8',
  cardColor: '#000000',
  cardOpacity: 60,
  cardBlur: 20,
  cardBorderStyle: 'subtle',
  cardBorderColor: '',
  cardBorderRadius: 16,
  iconStyle: 'default',
  iconSize: 40,
  linkStyle: 'icon',
  avatarShape: 'circle',
  avatarSize: 96,
  avatarBorder: 'none',
  avatarBlur: 0,
  avatarPositionX: 50,
  avatarPositionY: 50,
  avatarScale: 100,
  separatorStyle: 'gradient',
  cardAnimation: 'none',
  cardHover: 'none'
}

export interface SectionSettings {
  order: string[]
  visibility: Record<string, boolean>
  titles: Record<string, string>
  showTitles: boolean
}

export const DEFAULT_SECTION_SETTINGS: SectionSettings = {
  order: ['links', 'games', 'clips', 'friends', 'discord', 'youtube', 'twitch', 'spotify', 'setup'],
  visibility: { links: true, games: true, clips: true, friends: true, discord: true, youtube: true, twitch: true, spotify: true, setup: true },
  titles: {},
  showTitles: true
}

export const DEFAULT_SECTION_TITLES: Record<string, string> = {
  links: 'My Links',
  games: 'Games I Play',
  clips: 'My Clips',
  friends: 'My Friends',
  youtube: 'YouTube',
  twitch: 'Twitch',
  spotify: 'Spotify',
  setup: 'My Setup'
}

export interface CharacterSettingsData {
  display_name: string | null
  bio: string | null
  location: string | null
  pronouns: string | null
  avatar_id: string | null
  avatar_url: string | null
  background_id: string | null
  background_url: string | null
  cursor_id: string | null
  cursor_url: string | null
  layout: string
  text: TextSettings
  background: BackgroundSettings
  audio: AudioSettings
  theme: ThemeSettings
  sections: SectionSettings
  widgets: WidgetSettings
  comments: CommentSettings
  youtube: YouTubeEmbedSettings
  twitch: TwitchEmbedSettings
  spotify: SpotifyEmbedSettings
  privacy?: { showOnLeaderboard?: boolean } | null
}

export const useAuthStore = defineStore('auth', () => {
  const supabase = useSupabaseClient<Database>()
  const user = useSupabaseUser()

  const character = ref<Character | null>(null)
  const characterSettings = ref<CharacterSettingsData | null>(null)
  const subscription = ref<SubscriptionData | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastUserId = ref<string | null>(null)

  const userId = computed(() => user.value?.sub || null)
  const userEmail = computed(() => user.value?.email || null)

  // Staff role (admin/manager/moderator) from app_metadata
  const role = ref<StaffRole | null>(null)
  const isAdmin = computed(() => role.value === 'admin')
  const isManager = computed(() => role.value === 'manager')
  const isModerator = computed(() => role.value === 'moderator')
  const isStaff = computed(() => role.value !== null)

  // Plan computed helpers
  const plan = computed<PlanName>(() => subscription.value?.plan ?? 'free')
  const planStatus = computed(() => subscription.value?.status ?? null)
  const isPro = computed(() => plan.value === 'pro' || plan.value === 'elite')
  const isElite = computed(() => plan.value === 'elite')
  const hasUsedTrial = computed(() => subscription.value?.hasUsedTrial ?? false)
  const cancelAtPeriodEnd = computed(() => subscription.value?.cancelAtPeriodEnd ?? false)
  const currentPeriodEnd = computed(() => subscription.value?.currentPeriodEnd ?? null)
  const downgradedAt = computed(() => subscription.value?.downgradedAt ?? null)
  const previousPlan = computed(() => subscription.value?.previousPlan ?? null)
  const paymentType = computed<'subscription' | 'lifetime'>(() => subscription.value?.paymentType ?? 'subscription')
  const isLifetime = computed(() => subscription.value?.isLifetime ?? false)

  // Add-on helpers (standalone monthly modules, independent of the plan)
  const addons = computed<Partial<Record<AddonKey, AddonState>>>(() => subscription.value?.addons ?? {})

  function hasAddon(addon: AddonKey): boolean {
    return addons.value[addon]?.active ?? false
  }

  function addonState(addon: AddonKey): AddonState | null {
    return addons.value[addon] ?? null
  }

  function canAccess(feature: string, value: string): boolean {
    const featureConfig = PLAN_FEATURES[plan.value]
    const allowed = featureConfig[feature as keyof typeof featureConfig]
    if (Array.isArray(allowed)) {
      return (allowed as readonly string[]).includes(value)
    }
    return !!allowed
  }

  function canAccessBoolean(feature: 'richTextBio' | 'sectionReordering' | 'displayName' | 'showAsEmbed' | 'customEnterLabel' | 'videoBackgrounds' | 'backgroundGradient' | 'customSectionTitles' | 'comments'): boolean {
    return !!PLAN_FEATURES[plan.value][feature]
  }

  function getPlanLimit(resource: LimitKey): number {
    return PLAN_LIMITS[plan.value][resource]
  }

  function getUsage(resource: string): { current: number, limit: number | null } | null {
    return subscription.value?.usage?.[resource] ?? null
  }

  function getRequiredPlanFor(feature: string, value: string): PlanName | null {
    return getRequiredPlan(feature, value)
  }

  async function fetchCharacter() {
    if (!user.value?.sub) {
      error.value = 'User not authenticated'
      return null
    }

    isLoading.value = true
    error.value = null

    try {
      const data = await $fetch<{ character: Character, settings: any }>(`/api/character/${user.value.sub}`)

      character.value = data.character as Character
      characterSettings.value = {
        display_name: data.settings?.display_name ?? null,
        bio: data.settings?.bio ?? null,
        location: data.settings?.location ?? null,
        pronouns: data.settings?.pronouns ?? null,
        avatar_id: data.settings?.avatar_id ?? null,
        avatar_url: data.settings?.avatar_url ?? null,
        background_id: data.settings?.background_id ?? null,
        background_url: data.settings?.background_url ?? null,
        cursor_id: data.settings?.cursor_id ?? null,
        cursor_url: data.settings?.cursor_url ?? null,
        layout: data.settings?.layout ?? 'default',
        text: { ...DEFAULT_TEXT_SETTINGS, ...(data.settings?.text ?? {}) },
        background: { ...DEFAULT_BACKGROUND_SETTINGS, ...(data.settings?.background ?? {}) },
        audio: { ...DEFAULT_AUDIO_SETTINGS, ...(data.settings?.audio ?? {}) },
        theme: { ...DEFAULT_THEME_SETTINGS, ...(data.settings?.theme ?? {}) },
        sections: { ...DEFAULT_SECTION_SETTINGS, ...(data.settings?.sections ?? {}) },
        youtube: { ...DEFAULT_YOUTUBE_EMBED_SETTINGS, ...(data.settings?.youtube ?? {}) },
        twitch: { ...DEFAULT_TWITCH_EMBED_SETTINGS, ...(data.settings?.twitch ?? {}) },
        spotify: { ...DEFAULT_SPOTIFY_EMBED_SETTINGS, ...(data.settings?.spotify ?? {}) },
        privacy: data.settings?.privacy ?? null
      }
      return data.character
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch character'
      console.error('Error fetching character:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function fetchSubscription() {
    if (!user.value?.sub) return
    try {
      const data = await $fetch<SubscriptionData>('/api/subscription')
      subscription.value = data
    } catch {
      // Default to free plan on error
      subscription.value = null
    }
  }

  async function updateUser(updates: { email?: string }) {
    if (!user.value?.sub) {
      error.value = 'User not authenticated'
      return false
    }

    isLoading.value = true
    error.value = null

    try {
      if (updates.email && updates.email !== user.value.email) {
        const { error: updateError } = await supabase.auth.updateUser({
          email: updates.email
        })

        if (updateError) throw updateError

        await supabase.auth.refreshSession()
      }

      return true
    } catch (err: any) {
      error.value = err.message || 'Failed to update user info'
      console.error('Error updating user info:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const isBlocked = computed(() => !!character.value?.block)
  const isWhitelisted = computed(() => !!character.value?.whitelist)

  async function submitAppeal() {
    if (!user.value?.sub) {
      error.value = 'User not authenticated'
      return false
    }

    try {
      await $fetch(`/api/character/${user.value.sub}/block/appeal`, {
        method: 'POST'
      })
      await fetchCharacter()
      return true
    } catch (err: any) {
      error.value = err.data?.message || err.message || 'Failed to submit appeal'
      console.error('Error submitting appeal:', err)
      return false
    }
  }

  async function updateCharacter(updates: { username?: string, onboarding_completed?: boolean, onboarding?: OnboardingData }) {
    if (!user.value?.sub) {
      error.value = 'User not authenticated'
      return false
    }

    isLoading.value = true
    error.value = null

    try {
      const data = await $fetch(`/api/character/${user.value.sub}`, {
        method: 'PUT',
        body: updates
      })

      character.value = data as Character
      return true
    } catch (err: any) {
      error.value = err.data?.message || err.message || 'Failed to update character'
      console.error('Error updating character:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function initializeCharacter() {
    if (user.value && !character.value) {
      await fetchCharacter()
    }
  }

  async function signOut() {
    try {
      isLoading.value = true

      const { error: signOutError } = await supabase.auth.signOut()

      if (signOutError) throw signOutError

      clearStore()

      await navigateTo('/')

      return true
    } catch (err: any) {
      error.value = err.message || 'Failed to sign out'
      console.error('Error signing out:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  function clearStore() {
    character.value = null
    characterSettings.value = null
    subscription.value = null
    error.value = null
    role.value = null
  }

  watch(user, async (newUser) => {
    const currentUserId = newUser?.sub || null

    if (currentUserId !== lastUserId.value) {
      lastUserId.value = currentUserId

      if (currentUserId) {
        const { data } = await supabase.auth.getUser()
        role.value = resolveStaffRole(data.user?.app_metadata)

        await Promise.all([fetchCharacter(), fetchSubscription()])
      }
    }
  }, { immediate: true })

  return {
    character,
    characterSettings,
    subscription,
    isLoading,
    error,

    userId,
    userEmail,
    role,
    isAdmin,
    isManager,
    isModerator,
    isStaff,
    isBlocked,
    isWhitelisted,

    // Plan helpers
    plan,
    planStatus,
    isPro,
    isElite,
    hasUsedTrial,
    cancelAtPeriodEnd,
    currentPeriodEnd,
    downgradedAt,
    previousPlan,
    paymentType,
    isLifetime,
    addons,
    hasAddon,
    addonState,
    canAccess,
    canAccessBoolean,
    getPlanLimit,
    getUsage,
    getRequiredPlanFor,

    fetchCharacter,
    fetchSubscription,
    updateUser,
    updateCharacter,
    submitAppeal,
    initializeCharacter,
    signOut,
    clearStore
  }
})
